import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import {
  addNegativeToast,
  addNegativeToastWithErrorId,
  addPositiveToast,
} from 'modules/toastContainer';
import { CurrentSelection } from 'utils/contexts/selection';
import { RenameAssetFolderQuery } from 'api/queries/creativeLibrary';

type RenameAssetFolderResponseData = {
  renameAssetFolder: {
    success: {
      folderId: number;
      folderName: string;
    };
    failure: {
      id: number;
      name?: string;
    };
  };
};

type Variables = {
  newName: string;
  folderId?: number | null;
};

const displayText = {
  genericError: 'Could not rename folder',
};

export const useRenameAssetFolder = () => {
  const dispatch = useDispatch();

  const [renameAssetFolder, { loading }] = useMutation<RenameAssetFolderResponseData, Variables>(
    RenameAssetFolderQuery,
  );

  const renameFunction = useCallback(
    async (newName: string, folder?: CurrentSelection<number, string>) => {
      if (!folder) {
        return dispatch(addNegativeToast('Folder could not be renamed'));
      }
      try {
        const { errors, data } = await renameAssetFolder({
          variables: {
            folderId: folder.id,
            newName,
          },
        });

        if (errors || !data?.renameAssetFolder) {
          return dispatch(
            addNegativeToastWithErrorId(displayText.genericError, 'renameAssetFolder'),
          );
        }

        if (data.renameAssetFolder.failure) {
          return dispatch(
            addNegativeToastWithErrorId(
              <ResponseWrapper>
                <NamedFolder name={folder.name} /> could not be renamed
                {data.renameAssetFolder.failure.name ? (
                  <span>
                    {' '}
                    to <NamedFolder name={data.renameAssetFolder.failure.name || ''} /> folder.
                  </span>
                ) : (
                  '.'
                )}
              </ResponseWrapper>,
              'renameAssetFolder',
            ),
          );
        }

        dispatch(
          addPositiveToast(
            <ResponseWrapper>
              <NamedFolder name={folder.name} /> was successfully renamed to{' '}
              <NamedFolder name={data.renameAssetFolder.success.folderName} /> folder.
            </ResponseWrapper>,
          ),
        );
      } catch (error) {
        return dispatch(addNegativeToastWithErrorId(displayText.genericError, 'renameAssetFolder'));
      }
    },
    [renameAssetFolder],
  );

  return {
    isLoading: loading,
    renameAssetFolder: renameFunction,
  };
};
